// frontend/src/utils/unicodeText.js
//
// Unicode look-alike text mapping — the engine behind /tools/linkedin-text-formatter.
//
// WHAT THIS ACTUALLY DOES, AND WHY IT MATTERS FOR THE COPY ON THE PAGE.
// LinkedIn has no rich-text formatting in posts. Every "bold LinkedIn post" you
// have seen is not bold text — it is DIFFERENT CHARACTERS. The letter A is
// swapped for U+1D5D4 MATHEMATICAL SANS-SERIF BOLD CAPITAL A, a distinct
// codepoint that merely looks bold. Nothing is styled; the alphabet is replaced.
//
// That has three consequences the honest version of this tool must state:
//   1. ACCESSIBILITY. Screen readers announce these by their Unicode names or
//      skip them entirely. Verified 2026-08-18 against Scope for Business and
//      RNIB, both of which advise against Unicode text on social media outright.
//      Every competitor on this SERP (typegrow, authoredup, typefully, poper.ai,
//      linkedin-makeover) is silent about it. Saying so is our differentiator.
//   2. SEARCH. LinkedIn's own search indexes the real characters, so a headline
//      written in pseudo-bold is invisible to anyone searching those words.
//   3. It cannot be undone by LinkedIn — the characters are the content. `strip()`
//      below maps them back, which is why this file exports it.
//
// IMPLEMENTATION NOTE: every one of these blocks lives above the BMP, so each
// output character is a SURROGATE PAIR in JavaScript. Anything using .length,
// .charAt or a [...] split on the result will be wrong. Use codePointAt and
// String.fromCodePoint throughout, and count with [...str].length.

/* ── The blocks ───────────────────────────────────────────────────────────────
   Each style names the codepoint of 'A', 'a' and '0' in its block. Where a block
   is incomplete, Unicode scattered the missing letters into Letterlike Symbols
   and they are listed as exceptions — omitting these is the single most common
   bug in these tools and produces a visible hole in the output. */

const STYLES = {
  'sans-bold':        { A: 0x1D5D4, a: 0x1D5EE, zero: 0x1D7EC },
  'sans-italic':      { A: 0x1D608, a: 0x1D622 },
  'sans-bold-italic': { A: 0x1D63C, a: 0x1D656 },
  'serif-bold':       { A: 0x1D400, a: 0x1D41A, zero: 0x1D7CE },
  'serif-italic':     { A: 0x1D434, a: 0x1D44E, ex: { h: 0x210E } },
  'monospace':        { A: 0x1D670, a: 0x1D68A, zero: 0x1D7F6 },
  'script': {
    A: 0x1D49C, a: 0x1D4B6,
    ex: { B: 0x212C, E: 0x2130, F: 0x2131, H: 0x210B, I: 0x2110, L: 0x2112,
          M: 0x2133, R: 0x211B, e: 0x212F, g: 0x210A, o: 0x2134 },
  },
  'fraktur': {
    A: 0x1D504, a: 0x1D51E,
    ex: { C: 0x212D, H: 0x210C, I: 0x2111, R: 0x211C, Z: 0x2128 },
  },
  'double-struck': {
    A: 0x1D538, a: 0x1D552, zero: 0x1D7D8,
    ex: { C: 0x2102, H: 0x210D, N: 0x2115, P: 0x2119, Q: 0x211A, R: 0x211D, Z: 0x2124 },
  },
}

// Underline and strikethrough are not alphabets — they are COMBINING marks that
// attach to the preceding character. They therefore stack with any style above,
// and they are the two that most often break when pasted into other apps.
const COMBINING = {
  underline: '̲',
  strikethrough: '̶',
}

/** Human labels, and whether the style survives LinkedIn search (none do). */
export const STYLE_LIST = [
  ['sans-bold', 'Bold'],
  ['sans-italic', 'Italic'],
  ['sans-bold-italic', 'Bold italic'],
  ['serif-bold', 'Bold serif'],
  ['serif-italic', 'Italic serif'],
  ['underline', 'Underline'],
  ['strikethrough', 'Strikethrough'],
  ['monospace', 'Monospace'],
  ['script', 'Script'],
  ['fraktur', 'Fraktur'],
  ['double-struck', 'Outline'],
]

const isUpper = c => c >= 'A' && c <= 'Z'
const isLower = c => c >= 'a' && c <= 'z'
const isDigit = c => c >= '0' && c <= '9'

/**
 * Convert plain text into a Unicode look-alike style.
 * Characters the style cannot represent (punctuation, spaces, emoji, accented
 * letters) pass through untouched rather than being dropped.
 */
export function toStyle (text, style) {
  const src = String(text == null ? '' : text)
  if (!style || style === 'plain') return src

  if (COMBINING[style]) {
    // Attach the mark to every visible character. Spaces are skipped: a
    // combining mark on a space renders as a floating artefact in most fonts.
    return [...src].map(ch => (ch.trim() ? ch + COMBINING[style] : ch)).join('')
  }

  const s = STYLES[style]
  if (!s) return src

  return [...src].map(ch => {
    if (s.ex && s.ex[ch] !== undefined) return String.fromCodePoint(s.ex[ch])
    if (isUpper(ch)) return String.fromCodePoint(s.A + (ch.charCodeAt(0) - 65))
    if (isLower(ch)) return String.fromCodePoint(s.a + (ch.charCodeAt(0) - 97))
    if (isDigit(ch) && s.zero !== undefined) {
      return String.fromCodePoint(s.zero + (ch.charCodeAt(0) - 48))
    }
    return ch
  }).join('')
}

/* ── Reverse mapping ─────────────────────────────────────────────────────────
   Built once from the same tables, so it cannot drift from toStyle(). */

const REVERSE = (() => {
  const m = new Map()
  for (const [name, s] of Object.entries(STYLES)) {
    for (let i = 0; i < 26; i++) {
      m.set(s.A + i, String.fromCharCode(65 + i))
      m.set(s.a + i, String.fromCharCode(97 + i))
    }
    if (s.zero !== undefined) {
      for (let i = 0; i < 10; i++) m.set(s.zero + i, String(i))
    }
    if (s.ex) for (const [plain, cp] of Object.entries(s.ex)) m.set(cp, plain)
    void name
  }
  return m
})()

/** Map styled characters back to plain ASCII and remove combining marks. */
export function strip (text) {
  const src = String(text == null ? '' : text)
  return [...src]
    .map(ch => {
      const cp = ch.codePointAt(0)
      if (REVERSE.has(cp)) return REVERSE.get(cp)
      if (ch === COMBINING.underline || ch === COMBINING.strikethrough) return ''
      return ch
    })
    .join('')
}

/** True if the character is a styled look-alike rather than a plain character. */
function isStyled (ch) {
  const cp = ch.codePointAt(0)
  return REVERSE.has(cp) || ch === COMBINING.underline || ch === COMBINING.strikethrough
}

/**
 * How much of the text is Unicode look-alike, and therefore unreadable to a
 * screen reader and invisible to LinkedIn search. This drives the warning on the
 * page — the number is the argument, not the adjective.
 */
export function accessibilityReport (text) {
  const chars = [...String(text == null ? '' : text)]
  const visible = chars.filter(c => c.trim())
  const styled = visible.filter(isStyled)
  const pct = visible.length ? Math.round((styled.length / visible.length) * 100) : 0
  return {
    total: visible.length,
    styled: styled.length,
    pct,
    // Thresholds follow the guidance from Scope and RNIB: short emphasis is a
    // reasonable trade, a post written entirely in look-alikes is not.
    level: pct === 0 ? 'none' : pct <= 15 ? 'ok' : pct <= 40 ? 'warn' : 'bad',
  }
}

/** Characters as LinkedIn counts them — codepoints, not UTF-16 units. */
export const countChars = text => [...String(text == null ? '' : text)].length

// LinkedIn truncates a feed post with "…see more". The cut is around 210
// characters on desktop and shorter on mobile; it is not a published number, so
// the page presents it as approximate rather than stating it as fact.
export const SEE_MORE_AT = 210
export const POST_LIMIT = 3000

/** Split text at the "see more" fold so the page can preview what is visible. */
export function foldPreview (text, at = SEE_MORE_AT) {
  const chars = [...String(text == null ? '' : text)]
  if (chars.length <= at) return { visible: chars.join(''), hidden: '', folded: false }
  return {
    visible: chars.slice(0, at).join(''),
    hidden: chars.slice(at).join(''),
    folded: true,
  }
}
